import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/generated/types';
import { useProject } from '@/pages/projects/hooks/useProject';
import { useInviteUser } from '@/pages/projects/hooks/useInviteUser';
import { Input, ErrorPage, Paragraph, LoadingButton } from '@/components/shared';

export default function InviteUser() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const { project, loading, error } = useProject(id!);
  const { inviteUser, loading: inviteLoading, error: inviteError } = useInviteUser();

  const [email, setEmail] = useState('');

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await inviteUser(id!, email);
      navigate(`/projects/${id}`);
    } catch (err) {
      console.error('Failed to invite user:', err);
    }
  };

  const canManageMembers = !!(currentUser?.role === UserRole.Admin && project?.members?.some((m) => m.id === currentUser.id));

  if (error && !loading) return <ErrorPage message={`Erro: ${error.message}`} />;
  if (!project && !loading) return <ErrorPage message="Projeto não encontrado" />;
  if (!canManageMembers && !loading) return <ErrorPage message="Você não tem permissão para convidar usuários para este projeto" />;

  return (
    <div className="min-h-screen bg-primary-50 dark:bg-primary-950 py-24">
      <div className="container-elegant max-w-2xl">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={`/projects/${id}`}
            className="text-sm uppercase tracking-widest text-primary-600 dark:text-primary-400 hover:text-primary-950 dark:hover:text-white mb-4"
          >
            ← Voltar ao Projeto
          </Link>
          <h1 className="text-3xl font-light tracking-tight text-primary-950 dark:text-white mb-2">
            Convidar Usuário
          </h1>
          <Paragraph>
            {project?.name}
          </Paragraph>
        </div>

        {inviteError && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>
              Erro ao enviar convite: {inviteError.message}
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardContent className="p-8">
            <h3 className="text-lg font-light text-primary-950 dark:text-white mb-4">Enviar Convite por E-mail</h3>
            <p className="text-primary-700 dark:text-primary-300 mb-6">
              Digite o endereço de e-mail da pessoa que você deseja convidar para participar deste projeto.
              Ela receberá um convite por e-mail com instruções para se juntar ao projeto.
            </p>

            <form onSubmit={handleInvite} className="space-y-6">
              <Input
                label="Endereço de E-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                variant="primary"
                loading={loading}
                required
              />

              <div className="flex gap-4">
                <LoadingButton
                  type="submit"
                  loading={inviteLoading}
                  loadingText="ENVIANDO CONVITE..."
                  disabled={!email}
                  className="bg-primary-950 dark:bg-white text-white dark:text-primary-950 px-8 py-3 text-sm uppercase tracking-widest font-light hover:bg-primary-800 dark:hover:bg-primary-100 transition-colors duration-300"
                >
                  ENVIAR CONVITE
                </LoadingButton>
                <Button asChild variant="outline" size="lg">
                  <Link to={`/projects/${id}`}>
                    CANCELAR
                  </Link>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Current Members */}
        <Card className="mt-8">
          <CardContent className="p-8">
            <h3 className="text-lg font-light text-primary-950 dark:text-white mb-4">Membros Atuais do Projeto</h3>
            <div className="space-y-3">
              {loading ? (
                // Skeleton loading for members
                Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-primary-100 dark:border-primary-800 last:border-b-0">
                    <div className="space-y-2">
                      <Skeleton className="rounded w-32 h-4" />
                      <Skeleton className="rounded w-48 h-3" />
                      <Skeleton className="rounded w-20 h-3" />
                    </div>
                  </div>
                ))
              ) : (
                project?.members?.map((member) => (
                  <div key={member.id} className="flex items-center justify-between py-2 border-b border-primary-100 dark:border-primary-800 last:border-b-0">
                    <div>
                      <div className="text-primary-950 dark:text-white font-medium">{member.fullName}</div>
                      <div className="text-sm text-primary-600 dark:text-primary-400">{member.email}</div>
                      <div className="text-xs uppercase tracking-widest text-primary-500 dark:text-primary-500">{member.role}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}